import { CLASSROOM_RULE_LIMITS, INVITE_TTL_MS, type ErrorMsg, type FighterDTO, type FriendDTO, type InviteView, type RoomMode, type RoomView } from '@shared/protocol/messages';
import type { App } from '../../app/App';
import { Scene } from '../core/Scene';
import { RULES, skillDef } from '../combat/rules';
import { fitToBudget } from '../combat/budget';
import { heroDrawing, heroFighter } from '../systems/hero';
import { isBlank } from '../../drawing/strokes';
import { drawingKey } from '../../drawing/DrawingTextures';
import { ensureOnline } from '../../networking/session';
import { canUseWorldChat, isKid } from '../systems/policy';
import { heroClass } from '../data/presets';
import { worldChatPanel } from '../../ui/worldChat';
import { button, statChip, stickyNote } from '../../ui/components';
import { h, setChildren } from '../../ui/dom';
import { t } from '../../i18n';
import { ensureDrawingRules } from '../../ui/drawingRules';
import { shareRoom } from '../../networking/quickFight';
import { openRankings, rankingsPanel } from '../../ui/rankings';

const uploadedDrawings = new Map<string, string>();

export type MultiplayerParams = {
  /** A creator's saved monster is preselected for the existing room/invite flow. */
  monsterId?: string;
  joinCode?: string;
  /** Invite this friend (from the Friends tab): reuses or opens a room. */
  inviteFriend?: { id: string; name: string };
  /** Accept this invite (from the invite note). */
  acceptInvite?: InviteView;
} | void;

/** "Battle With Friends": private rooms joined by code or invite, server-authoritative battles. */
export class MultiplayerScene extends Scene<MultiplayerParams> {
  private body!: HTMLElement;
  private connChip!: HTMLElement;
  private fighterChoice = 'hero';
  private incoming: { fromId: string; fromName: string } | null = null;
  private busy = false;
  /** World chat panel (13+), kept across renders so typing is not lost. */
  private chatPanel: HTMLElement | null = null;
  /** What the page was opened to do (join a code, invite, accept); done once we are online. */
  private pending: Exclude<MultiplayerParams, void> = {};
  /** Accepted friends with presence, for the one-tap Battle buttons (null = not loaded). */
  private friends: FriendDTO[] | null = null;
  /** A quick match in progress: when a bot takes the seat if no player turns up. */
  private searching: { botAt: number } | null = null;

  constructor(app: App) {
    super(app, 'scene-multiplayer page');
  }

  enter(params: MultiplayerParams): void {
    const app = this.app;
    if (params?.monsterId) this.fighterChoice = `monster:${params.monsterId}`;
    this.body = h('div', { class: 'mp-body' });
    this.connChip = h('span', { class: 'conn-chip' });
    this.dom.append(
      h('div', { class: 'spiral', 'aria-hidden': 'true' }),
      h(
        'header',
        { class: 'mp-top' },
        button(t('← Back'), { variant: 'ghost', small: true, sfx: app, onClick: () => void app.scenes.go('menu', undefined, 'page-back') }),
        h('h1', { class: 'page-heading' }, t('Battle With Friends')),
        this.connChip,
        button(h('span', null, '🏆 ', t('Rankings')), { small: true, color: 'yellow', sfx: app, onClick: () => void openRankings(app) }),
      ),
      this.body,
    );
    this.d.add(
      app.mp.on('state', (s) => {
        this.render();
        if (s === 'online') void this.onlineNow();
      }),
    );
    for (const ev of ['friend.accepted', 'friend.removed'] as const) this.d.add(app.mp.on(ev, () => void this.loadFriends()));
    this.d.add(
      app.mp.on('friend.online', (m) => {
        const f = this.friends?.find((x) => x.id === m.id);
        if (!f) return;
        f.online = m.online;
        if (!app.room.room) this.render();
      }),
    );
    this.d.add(app.room.on('room', () => this.render()));
    this.d.add(
      app.room.on('challenge', (m) => {
        this.incoming = m;
        app.sfx.play('pop');
        this.render();
      }),
    );
    this.d.add(
      app.room.on('declined', () => {
        app.toast(t('Your challenge was declined. Maybe next recess!'), 'info');
        this.render();
      }),
    );
    this.d.add(app.mp.on('invite.declined', () => this.render()));
    this.d.add(
      app.mp.on('match.status', (m) => {
        this.searching = m.searching ? { botAt: m.botAt ?? Date.now() + 10_000 } : null;
        this.render();
      }),
    );
    // Leaving the page stops a search (the battle itself is opened by the app).
    this.d.add(() => {
      if (this.searching && app.mp.state === 'online' && !app.room.room) void app.mp.send({ t: 'match.cancel' }).catch(() => {});
    });
    if (canUseWorldChat(app.store.state)) {
      const chat = worldChatPanel(app);
      this.chatPanel = chat.el;
      this.d.add(chat.dispose);
    }
    this.render();
    this.pending = params || {};
    void this.boot();
  }

  private async boot(): Promise<void> {
    const app = this.app;
    if (app.mp.state !== 'online') {
      try {
        await app.api.health();
      } catch {
        this.renderOffline();
        return;
      }
      if (!app.store.state.online) {
        this.render();
        return;
      }
      await this.connect();
      if ((app.mp.state as string) !== 'online') return; // connect() changed it; TS still narrows the field
    }
    this.render();
    await this.onlineNow();
  }

  /** Once online: load the friends list and do what the page was opened for (a room link, an invite). */
  private async onlineNow(): Promise<void> {
    void this.loadFriends();
    const p = this.pending;
    this.pending = {};
    if (p.joinCode) await this.join(p.joinCode);
    if (p.inviteFriend) await this.invite(p.inviteFriend);
    if (p.acceptInvite) await this.acceptInvite(p.acceptInvite);
  }

  private async loadFriends(): Promise<void> {
    try {
      this.friends = (await this.app.api.friends()).friends;
    } catch {
      this.friends = [];
    }
    if (!this.app.room.room && this.app.mp.state === 'online') this.render();
  }

  private async connect(): Promise<void> {
    const app = this.app;
    if (!(await ensureOnline(app, !app.store.state.online))) return this.render();
    try {
      await app.mp.connect(app.store.state.online!.token);
    } catch (err) {
      app.toast(err instanceof Error ? err.message : t('Could not connect'), 'error');
    }
    this.render();
  }

  private renderOffline(): void {
    setChildren(
      this.body,
      stickyNote(
        'pink',
        h('h2', null, t('The battle server is offline')),
        h('p', null, t('Online rooms need the Go game server (see README → "Run the backend"). Your adventure, monsters and notebook work fine without it.')),
        h('div', { class: 'row-actions' }, button(t('Try again'), { color: 'yellow', sfx: this.app, onClick: () => void this.boot() }), button(t('Play single-player'), { variant: 'primary', color: 'green', sfx: this.app, onClick: () => void this.app.scenes.go('notebook', { tab: 'quests' }) })),
      ),
    );
    this.renderConn('offline');
  }

  private renderConn(state: string): void {
    this.connChip.className = `conn-chip ${state}`;
    this.connChip.textContent =
      { offline: t('● offline'), connecting: t('● connecting…'), online: t('● online as {name}', { name: this.app.store.state.online?.name ?? '?' }), reconnecting: t('● reconnecting…') }[state] ?? state;
  }

  private render(): void {
    const app = this.app;
    this.renderConn(app.mp.state);
    if (app.mp.state !== 'online') {
      if (app.mp.state === 'reconnecting' || app.mp.state === 'connecting') {
        setChildren(this.body, stickyNote('yellow', h('p', null, t('Connecting to the classroom… (we will put you back in your room automatically)'))));
        return;
      }
      setChildren(
        this.body,
        stickyNote(
          'blue',
          h('h2', null, t('Join the class')),
          h('p', null, app.store.state.online ? t('Signed in as {name}.', { name: app.store.state.online.name }) : t('Pick a nickname so friends know who they are fighting.')),
          button(app.store.state.online ? t('Connect') : t('Pick a nickname'), { variant: 'primary', color: 'yellow', sfx: app, onClick: () => void this.connect() }),
        ),
      );
      return;
    }
    const room = app.room.room;
    setChildren(this.body, room ? this.renderRoom(room) : this.renderNoRoom(), this.chatPanel);
  }

  private fighterSelect(): HTMLElement {
    const app = this.app;
    const hero = app.store.hero;
    const opts: [string, string][] = [];
    if (hero) opts.push(['hero', t('My hero: {name} (Lv {level})', { name: hero.name, level: hero.level })]);
    for (const m of app.store.state.monsters) opts.push([`monster:${m.id}`, t('Monster: {name}', { name: m.name })]);
    if (!opts.length) return h('p', { class: 'hand-hint' }, t('Create a hero or draw a monster first!'));
    if (!opts.some(([v]) => v === this.fighterChoice)) this.fighterChoice = opts[0]![0];
    const sel = h('select', { class: 'hand-select', 'aria-label': t('Fighter') }, opts.map(([v, label]) => h('option', { value: v, selected: v === this.fighterChoice }, label)));
    sel.addEventListener('change', () => (this.fighterChoice = sel.value));
    return h('label', { class: 'field' }, h('span', null, t('Fight as')), sel);
  }

  /** Build a loadout. Public rooms scale it into the arena budget; the server re-validates. */
  private async buildFighter(mode: RoomMode, freeStats: boolean): Promise<FighterDTO | null> {
    const app = this.app;
    const upload = async (d: Parameters<typeof drawingKey>[0] | null): Promise<string | undefined> => {
      if (!d || isBlank(d)) return undefined;
      const key = drawingKey(d);
      const cached = uploadedDrawings.get(key);
      if (cached) return cached;
      // Other players will see it: the drawing rules come first (else the preset doodle is used).
      if (!(await ensureDrawingRules(app))) return undefined;
      try {
        const res = await app.api.uploadDrawing(d);
        uploadedDrawings.set(key, res.id);
        return res.id;
      } catch {
        return undefined; // fall back to the preset doodle
      }
    };
    if (this.fighterChoice === 'hero') {
      const hero = app.store.hero;
      if (!hero) return null;
      const f = heroFighter(hero, mode, freeStats, await upload(heroDrawing(hero)), this.app.store.state.itemLevels);
      // Kid mode: other players never see a name a child typed, so the hero fights under its class name.
      if (isKid(app.store.state)) f.name = t(heroClass(hero.classId).name).slice(0, 24);
      return f;
    }
    const m = app.store.state.monsters.find((x) => `monster:${x.id}` === this.fighterChoice);
    if (!m) return null;
    let block = { hp: m.hp, atk: m.atk, def: m.def, luck: m.luck, skill: m.skill };
    if (!(mode === 'classroom' && freeStats)) block = fitToBudget(block, RULES.budget.pvpPublicBudget);
    const drawingId = await upload(m.drawing);
    return { name: m.name.slice(0, 24), kind: 'monster', maxHp: block.hp, atk: block.atk, def: block.def, luck: block.luck, skill: block.skill, look: drawingId ? { drawingId, preset: m.preset ?? 'gum_blob' } : { preset: m.preset ?? 'gum_blob' } };
  }

  private async run(fn: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await fn();
    } catch (err) {
      this.app.toast((err as ErrorMsg).message ?? t('Something went wrong'), 'error');
    } finally {
      this.busy = false;
      this.render();
    }
  }

  private async join(code: string): Promise<void> {
    await this.run(async () => {
      const fighter = await this.buildFighter('public', false);
      if (!fighter) throw { message: t('Pick a fighter first.') };
      this.app.quick.armNext();
      await this.app.mp.send({ t: 'room.join', code: code.trim().toUpperCase(), fighter });
    });
  }

  /** Invite a friend into my room; the server opens a public room if I have none. */
  private async invite(friend: { id: string; name: string }): Promise<void> {
    await this.run(async () => {
      const app = this.app;
      const fighter = app.room.room ? null : await this.buildFighter('public', false);
      if (!app.room.room && !fighter) throw { message: t('Create a hero or draw a monster first!') };
      if (app.room.room) app.quick.armCurrent();
      else app.quick.armNext();
      await app.mp.send({ t: 'invite.send', friendId: friend.id, ...(fighter ? { fighter } : {}) });
      app.outgoingInvite = { id: friend.id, name: friend.name, until: Date.now() + INVITE_TTL_MS };
      app.toast(t('Invite sent to {name}!', { name: friend.name }), 'success');
    });
  }

  /** Join the room a friend invited me to, leaving a room I am waiting in. */
  private async acceptInvite(inv: InviteView): Promise<void> {
    await this.run(async () => {
      const app = this.app;
      const room = app.room.room;
      if (room?.code === inv.roomCode) return;
      if (room?.status === 'battle') throw { message: t('Finish your battle first!') };
      const fighter = await this.buildFighter('public', false);
      if (!fighter) throw { message: t('Create a hero or draw a monster first!') };
      if (room) await app.mp.send({ t: 'room.leave' });
      app.quick.armNext();
      await app.mp.send({ t: 'invite.respond', fromId: inv.fromId, accept: true, fighter });
    });
  }

  /** A numbered way to start a battle (1, 2, 3 on the page). */
  private step(n: number, title: string, hint: string, ...content: (HTMLElement | null)[]): HTMLElement {
    return h('section', { class: `paper-card taped mp-card mp-step mp-step-${n}` }, h('h2', null, h('span', { class: 'mp-step-no', 'aria-hidden': 'true' }, String(n)), title), h('p', { class: 'hand-hint' }, hint), ...content);
  }

  /** Quick match: anyone who is looking right now, or a bot copy of a real player's hero after 10 s. */
  private quickMatch(): HTMLElement {
    const app = this.app;
    if (this.searching) {
      const left = h('b', { class: 'mp-countdown' });
      const tick = (): void => {
        const s = Math.max(0, Math.ceil(((this.searching?.botAt ?? 0) - Date.now()) / 1000));
        left.textContent = s > 0 ? t('{n} s', { n: s }) : t('a bot is coming…');
      };
      tick();
      const timer = window.setInterval(() => (left.isConnected ? tick() : window.clearInterval(timer)), 500);
      return h(
        'section',
        { class: 'paper-card taped mp-card mp-quickmatch searching' },
        h('h2', null, '🔎 ', t('Looking for an opponent…')),
        h('p', null, t('If nobody turns up in'), ' ', left, ' ', t("you battle a bot 🤖 (a copy of a real player's hero).")),
        button(t('Cancel'), { variant: 'ghost', sfx: app, onClick: () => void this.run(() => app.mp.send({ t: 'match.cancel' })) }),
      );
    }
    return h(
      'section',
      { class: 'paper-card taped mp-card mp-quickmatch' },
      button(h('span', null, '⚔️ ', t('Find an opponent!')), {
        variant: 'primary',
        color: 'pink',
        sfx: app,
        onClick: () =>
          void this.run(async () => {
            const fighter = await this.buildFighter('public', false);
            if (!fighter) throw { message: t('Pick a fighter first.') };
            await app.mp.send({ t: 'match.find', fighter });
          }),
      }),
      h('p', { class: 'hand-hint' }, t("Battle someone who is looking right now. Nobody within 10 seconds? A bot 🤖 copied from a real player's hero steps in (half the trophies).")),
    );
  }

  /** Step 1: friends online right now, each with a Battle button. */
  private friendsStep(): HTMLElement {
    const app = this.app;
    const online = (this.friends ?? []).filter((f) => f.online);
    const rows =
      this.friends === null
        ? h('p', { class: 'hand-hint' }, t('Looking for your friends…'))
        : online.length
          ? h(
              'ul',
              { class: 'friend-list quick-friends' },
              online.map((f) =>
                h(
                  'li',
                  { class: 'friend is-online' },
                  h('span', { class: 'presence-dot', role: 'img', 'aria-label': t('online now') }),
                  h('b', null, f.name),
                  h('small', null, ` ${f.trophies} 🏆`),
                  h('span', { class: 'friend-actions' }, button(t('Battle!'), { small: true, variant: 'primary', color: 'pink', sfx: app, onClick: () => void this.invite({ id: f.id, name: f.name }) })),
                ),
              ),
            )
          : h(
              'div',
              { class: 'mp-empty' },
              h('p', null, this.friends.length ? t('None of your friends is online right now.') : t('No friends added yet.')),
              button(t('Add friends'), { small: true, sfx: app, onClick: () => void app.scenes.go('notebook', { tab: 'friends' }, 'page') }),
            );
    return this.step(1, t('Friends online now'), t('Tap Battle: they get an invite and the battle starts when they say yes.'), rows);
  }

  /** Step 2: a room link to send in any chat app. */
  private linkStep(): HTMLElement {
    const app = this.app;
    return this.step(
      2,
      t('Invite with a link'),
      t('Make a room and send the link (LINE, Messenger…). The battle starts by itself when your friend opens it.'),
      button(t('Make a room and send the link'), {
        variant: 'primary',
        color: 'yellow',
        sfx: app,
        onClick: () =>
          void this.run(async () => {
            const fighter = await this.buildFighter('public', false);
            if (!fighter) throw { message: t('Pick a fighter first.') };
            app.quick.armNext();
            const made = new Promise<RoomView | null>((resolve) => {
              const off = app.room.on('room', (r) => {
                off();
                resolve(r);
              });
              window.setTimeout(() => {
                off();
                resolve(app.room.room);
              }, 5000);
            });
            await app.mp.send({ t: 'room.create', mode: 'public', fighter });
            const room = app.room.room ?? (await made);
            if (room) await shareRoom(app, room.code);
          }),
      }),
    );
  }

  /** Step 3: a code a friend wrote down. */
  private codeStep(): HTMLElement {
    const codeIn = h('input', { class: 'hand-input code big', maxLength: 6, placeholder: t('CODE'), 'aria-label': t('Room code'), autocomplete: 'off' });
    codeIn.addEventListener('input', () => (codeIn.value = codeIn.value.toUpperCase().replace(/[^A-Z0-9]/g, '')));
    const go = (): void => {
      if (codeIn.value.length !== 6) return this.app.toast(t('Room codes have 6 characters.'), 'info');
      void this.join(codeIn.value);
    };
    codeIn.addEventListener('keydown', (e) => e.key === 'Enter' && go());
    return this.step(3, t('Have a room code?'), t('Type the 6-letter code your friend gave you.'), h('div', { class: 'mp-code-row' }, codeIn, button(t('Join'), { variant: 'primary', color: 'pink', sfx: this.app, onClick: go })));
  }

  private renderNoRoom(): HTMLElement {
    const app = this.app;
    // The folded "more options" room is mainly for teachers: it opens on classroom rules.
    let mode: RoomMode = 'classroom';
    const L = CLASSROOM_RULE_LIMITS;
    const timeIn = h('input', { type: 'range', class: 'hand-range', min: String(L.turnTimeoutMs.min / 1000), max: String(L.turnTimeoutMs.max / 1000), value: '20', 'aria-label': t('Turn timer seconds') });
    const timeLbl = h('span', null, t('{n}s', { n: 20 }));
    timeIn.addEventListener('input', () => (timeLbl.textContent = t('{n}s', { n: timeIn.value })));
    const roundsIn = h('input', { type: 'number', class: 'hand-input num', min: String(L.maxRounds.min), max: String(L.maxRounds.max), value: '15', 'aria-label': t('Max rounds') });
    const freeIn = h('input', { type: 'checkbox', checked: true, 'aria-label': t('Free stats') });
    const classroomBox = h(
      'div',
      { class: 'classroom-rules' },
      h('label', { class: 'field' }, h('span', null, t('Turn timer')), timeIn, timeLbl),
      h('label', { class: 'field' }, h('span', null, t('Max rounds')), roundsIn),
      h('label', { class: 'check-field' }, freeIn, h('span', null, t('Free stats (no budget — practice/teaching)'))),
    );
    const modeBtns = (['public', 'classroom'] as const).map((m) => {
      const b = h('button', { type: 'button', class: `mode-btn ${m === mode ? 'active' : ''}`, role: 'radio', 'aria-checked': String(m === mode) }, m === 'public' ? t('Public · ranked') : t('Classroom · custom rules'));
      b.addEventListener('click', () => {
        mode = m;
        modeBtns.forEach((x) => {
          x.classList.toggle('active', x === b);
          x.setAttribute('aria-checked', String(x === b));
        });
        classroomBox.hidden = m !== 'classroom';
      });
      return b;
    });
    return h(
      'div',
      { class: 'mp-layout' },
      // Who fights: chosen once, used by every way to start below.
      h(
        'section',
        { class: 'mp-fighter sticky-note yellow' },
        this.fighterSelect(),
        h('p', { class: 'hand-hint' }, t('Online battles are fair: every fighter is scaled into a {budget}-point budget, so levels cannot buy wins. Wins earn trophies.', { budget: RULES.budget.pvpPublicBudget })),
      ),
      this.quickMatch(),
      h('div', { class: 'mp-steps' }, this.friendsStep(), this.linkStep(), this.codeStep()),
      h(
        'section',
        { class: 'paper-card taped mp-card mp-ranks' },
        h('h2', null, '🏆 ', t('Rankings')),
        rankingsPanel(app, 'trophies', { limit: 5, onMore: () => void openRankings(app) }),
      ),
      h(
        'details',
        { class: 'paper-card taped mp-card mp-more' },
        h('summary', null, h('h2', null, t('Classroom room (custom rules, not ranked)'))),
        h('div', { class: 'mode-toggle', role: 'radiogroup', 'aria-label': t('Room mode') }, modeBtns),
        h('p', { class: 'hand-hint' }, t('Public rooms are ranked: every fighter is scaled into a {budget}-point budget.', { budget: RULES.budget.pvpPublicBudget })),
        classroomBox,
        button(t('Create Room'), {
          variant: 'primary',
          color: 'yellow',
          sfx: app,
          onClick: () =>
            void this.run(async () => {
              const freeStats = mode === 'classroom' && freeIn.checked;
              const fighter = await this.buildFighter(mode, freeStats);
              if (!fighter) throw { message: t('Pick a fighter first.') };
              if (mode === 'public') app.quick.armNext();
              await app.mp.send({
                t: 'room.create',
                mode,
                fighter,
                ...(mode === 'classroom' ? { rules: { turnTimeoutMs: Number(timeIn.value) * 1000, maxRounds: Math.round(Number(roundsIn.value) || 15), freeStats } } : {}),
              });
            }),
        }),
      ),
    );
  }

  private renderRoom(room: RoomView): HTMLElement {
    const app = this.app;
    const me = app.room.playerId;
    const opponent = room.players.find((p) => p.id !== me);
    const iAmChallenged = room.challengerId !== null && room.challengerId !== me;
    const iChallenged = room.challengerId === me;
    if (iAmChallenged && !this.incoming && opponent) this.incoming = { fromId: opponent.id, fromName: opponent.name };
    if (!room.challengerId) this.incoming = null;
    if (opponent) app.outgoingInvite = null;
    const invited = app.outgoingInvite && app.outgoingInvite.until > Date.now() ? app.outgoingInvite : null;

    const players = room.players.map((p) =>
      h(
        'li',
        { class: `room-player ${p.connected ? 'on' : 'off'}` },
        h('span', { class: 'dot', title: p.connected ? t('connected') : t('disconnected') }),
        h('b', null, p.name),
        p.id === room.hostId ? h('span', { class: 'stamp small' }, t('HOST')) : null,
        p.id === me ? h('span', { class: 'hand-hint' }, ` ${t('(you)')}`) : null,
        h('div', { class: 'chips' }, h('span', null, `${p.fighter.name}: `), statChip('HP', p.fighter.maxHp), statChip('ATK', p.fighter.atk), statChip('DEF', p.fighter.def), statChip('LUCK', p.fighter.luck), h('small', null, ` ★ ${t(skillDef(p.fighter.skill).name)}`)),
        p.connected ? null : h('small', { class: 'hand-hint' }, t('disconnected — waiting for them to come back…')),
      ),
    );

    const actions: (HTMLElement | null)[] = [];
    if (room.status === 'battle') {
      actions.push(button(t('Return to battle'), { variant: 'primary', color: 'pink', sfx: app, onClick: () => app.room.battle && void app.scenes.go('battle', { mode: 'pvp' }) }));
    } else if (this.incoming && iAmChallenged) {
      actions.push(
        h('p', { class: 'challenge-note sticky-note pink' }, t('{name} challenges you!', { name: this.incoming.fromName })),
        button(t('Accept!'), { variant: 'primary', color: 'pink', sfx: app, onClick: () => void this.run(() => app.mp.send({ t: 'challenge.respond', accept: true })) }),
        button(t('Decline'), { variant: 'ghost', sfx: app, onClick: () => void this.run(() => app.mp.send({ t: 'challenge.respond', accept: false })) }),
      );
    } else if (opponent && room.status === 'lobby' && app.quick.isQuick(room.code) && room.mode === 'public') {
      actions.push(h('p', { class: 'hand-hint' }, t('{name} is here, the battle is starting…', { name: opponent.name })));
    } else if (opponent && room.status === 'lobby') {
      actions.push(
        iChallenged
          ? h('p', { class: 'hand-hint' }, t('Challenge sent — waiting for {name}…', { name: opponent.name }))
          : button(t('Challenge {name}!', { name: opponent.name }), { variant: 'primary', color: 'pink', sfx: app, onClick: () => void this.run(() => app.mp.send({ t: 'challenge.send' })) }),
      );
    } else if (invited) {
      actions.push(h('p', { class: 'hand-hint' }, t('Invite sent to {name} — waiting for them to hop in…', { name: invited.name })));
    } else {
      actions.push(
        h('p', { class: 'hand-hint' }, app.quick.isQuick(room.code) ? t('Send the link: the battle starts as soon as your friend opens it.') : t('Waiting for a friend to join with the code…')),
      );
    }
    if (!opponent && room.status !== 'battle') actions.push(button(t('Send link to a friend'), { variant: 'primary', color: 'yellow', sfx: app, onClick: () => void shareRoom(app, room.code) }));

    return h(
      'div',
      { class: 'mp-room' },
      h(
        'section',
        { class: 'paper-card taped room-card' },
        h('p', { class: 'hand-hint' }, t('Room code — tell your friend:')),
        h(
          'button',
          { class: 'room-code', type: 'button', title: t('Copy code'), on: { click: () => void navigator.clipboard?.writeText(room.code).then(() => app.toast(t('Code copied!'), 'success')) } },
          room.code,
        ),
        h(
          'p',
          null,
          room.mode === 'public'
            ? t('Public room · ranked · fair arena budget')
            : t('Classroom room · not ranked · {sec}s turns · {rounds} rounds', { sec: room.rules.turnTimeoutMs / 1000, rounds: room.rules.maxRounds }) + (room.rules.freeStats ? ` · ${t('free stats')}` : ''),
        ),
        h('ul', { class: 'room-players' }, players),
        h('div', { class: 'row-actions' }, actions),
        h(
          'div',
          { class: 'row-actions small' },
          room.status !== 'battle' ? this.fighterSelect() : null,
          room.status !== 'battle'
            ? button(t('Update fighter'), {
                small: true,
                sfx: app,
                onClick: () =>
                  void this.run(async () => {
                    const f = await this.buildFighter(room.mode, room.rules.freeStats);
                    if (f) await app.mp.send({ t: 'room.fighter', fighter: f });
                  }),
              })
            : null,
          button(t('Leave room'), { small: true, variant: 'danger', sfx: app, onClick: () => void this.run(() => app.mp.send({ t: 'room.leave' })) }),
        ),
      ),
    );
  }
}
